"use client"
// ds-color-ignore-file — Figma 473:2359 and the demo report's own colours;
// the ones the theme has are read from it, the rest are Figma's raw values.

/**
 * 05b — What you get, the five tiles inside the immersive section
 * (`home-v4-what-you-get.tsx`). One to one from Figma "Landing. template",
 * frame 473:2359 (Eugene, 29.09): a 1280 × 743 grid of 416 × 361 tiles, 16
 * apart across and 21 down, the report tile 848 wide.
 *
 * Every length below is a Figma pixel times `--px`, and `--px` is the grid's
 * width over 1280, so the grid keeps Figma's proportions at the width of the
 * text column it sits in (Eugene, 29.09). The widgets are the demo report's
 * own markup (staging, `full-report-en`): the home page's health report
 * block, the action plan's clinical follow-ups and the biomarker chart, at
 * the scale Figma captured them (0.828 and 0.772), inside Figma's glass
 * panels. Where the two differ, Figma wins: its capture has the bio-age photo
 * at plain 41 % (the report blends it hard-light), no grain and no cut-out
 * round the age marker.
 *
 * Tile backgrounds are Figma's renders of the blurred photo layers (its
 * progressive blur, flip and exposure included), exported at 2x. The report
 * tile's photo is a fill on the frame itself, so its exposure (0.23) is baked
 * into the file from a curve fitted to Figma's render. The files are on Blob
 * (`scripts/blob-upload-manifest.json`); the Figma sources, exports and
 * reference renders stay local, in `public/images/marketing/home-v4/immersive/`.
 *
 * The tiles' captions are ours: the two in Figma were copied from elsewhere
 * (Eugene, 29.09).
 */

import Image from "next/image"
import { useLocale } from "next-intl"
import { blobMediaUrl } from "@/lib/blob-url"

const img = (file: string) =>
  blobMediaUrl(`/images/marketing/home-v4/immersive/${file}`)

/** A Figma pixel, scaled with the grid. */
const p = (n: number) => `calc(${n} * var(--px))`

/** A tile's place in the order the tiles arrive in; see `.imt-tile`. */
const order = (i: number) => ({ "--i": i }) as React.CSSProperties

/**
 * The demo member's report, in every language on its own (Eugene, 29.09: the
 * English page carried Figma's German follow-up). The MRI tile matches its
 * pictures: the spine, then the brain. The spine is a healthy finding, so
 * the MRI is not shown only through a follow-up scan, and the third plan
 * item is vitamin D, not potassium (Sven, 08.10).
 */
const labels = {
  en: {
    plansHeading: "Know what to change first",
    plans: [
      ["Increase calcium intake", "1,000–1,200 mg a day"],
      ["Moderate aerobic exercise", "150 minutes a week"],
      ["Increase your vitamin D dose", "Retest in 3 months"],
    ],
    marker: "Glucose, fasting",
    normal: "Normal",
    reportHeading:
      "Your biological age, your radiology findings and every biomarker we tested, in one report",
    report: "Your health report",
    bioAge: "Biological age",
    years: "1 yr",
    bioCaption: "1 year younger than your age at the time of testing (34).",
    findings: "Findings",
    minor: "Minor",
    observation: "Observations",
    biomarkers: "Biomarkers",
    optimal: "optimal",
    inRange: "in range",
    outOfRange: "out of range",
    followUps: "Your MRI findings",
    followUpItems: [
      ["Spine looks healthy", "No signs of wear or disc protrusion"],
      [
        "Follow-up brain MRI",
        "Schedule a follow-up MRI scan to recheck the earlier finding",
      ],
    ],
  },
  de: {
    plansHeading: "Wissen, was Sie zuerst ändern sollten",
    plans: [
      ["Kalziumzufuhr erhöhen", "1'000–1'200 mg pro Tag"],
      ["Moderates Ausdauertraining", "150 Minuten pro Woche"],
      ["Vitamin D höher dosieren", "In 3 Monaten neu messen"],
    ],
    marker: "Glukose, nüchtern",
    normal: "Normal",
    reportHeading:
      "Ihr biologisches Alter, Ihre Radiologiebefunde und alle getesteten Biomarker in einem Bericht",
    report: "Ihr Gesundheitsbericht",
    bioAge: "Biologisches Alter",
    years: "1 J.",
    bioCaption: "1 Jahr jünger als Ihr Alter zum Testzeitpunkt (34).",
    findings: "Befunde",
    minor: "Gering",
    observation: "Beobachtungen",
    biomarkers: "Biomarker",
    optimal: "optimal",
    inRange: "im Bereich",
    outOfRange: "ausser Bereich",
    followUps: "Ihre MRI-Befunde",
    followUpItems: [
      [
        "Wirbelsäule unauffällig",
        "Ohne Zeichen von Abnutzung oder Bandscheibenvorwölbung",
      ],
      [
        "Kontroll-MRI des Gehirns",
        "Planen Sie ein Kontroll-MRI, um den früheren Befund zu prüfen",
      ],
    ],
  },
  fr: {
    plansHeading: "Savoir quoi changer en premier",
    plans: [
      ["Augmenter votre apport en calcium", "1’000–1’200 mg par jour"],
      ["Activité aérobie modérée", "150 minutes par semaine"],
      ["Augmenter la dose de vitamine D", "Nouvelle mesure dans 3 mois"],
    ],
    marker: "Glucose à jeun",
    normal: "Normal",
    reportHeading:
      "Votre âge biologique, vos résultats de radiologie et tous les biomarqueurs testés, dans un seul rapport",
    report: "Votre rapport de santé",
    bioAge: "Âge biologique",
    years: "1 an",
    bioCaption: "1 an de moins que votre âge au moment du test (34).",
    findings: "Résultats",
    minor: "Mineur",
    observation: "Observations",
    biomarkers: "Biomarqueurs",
    optimal: "optimal",
    inRange: "dans la norme",
    outOfRange: "hors normes",
    followUps: "Vos résultats d'IRM",
    followUpItems: [
      [
        "Colonne vertébrale sans particularité",
        "Aucun signe d'usure ni de protrusion discale",
      ],
      [
        "IRM de contrôle du cerveau",
        "Planifiez une IRM pour recontrôler le résultat précédent",
      ],
    ],
  },
}

/** Figma 473:1767, the marker history, oldest first. Fasting glucose, not
 *  Figma's total cholesterol: cholesterol has no lower limit, and the tile
 *  draws an out-of-range band on both sides (Eugene, 15.10). The values and
 *  the unit stay, all inside glucose's 3.9–5.6 mmol/L. */
const MARKER = 4.8
const HISTORY = [
  // value label (484:10823…27) and dot centre (484:10895…99), tile px
  { v: 5.1, label: [104.109, 240.724], dot: [110.368, 228.204] },
  { v: 4.8, label: [156.617, 247.083], dot: [162.657, 234.464] },
  { v: 4.9, label: [213.711, 244.885], dot: [218.462, 230.852] },
  { v: 4.1, label: [270.805, 257.288], dot: [275.798, 244.78] },
  { v: 4.8, label: [324.844, 246.797], dot: [332.532, 230.852] },
]
/**
 * 484:10821/17/13/07, the dashed result lines. Figma draws each stroke on
 * one side of its line, so the centres sit 0.35 left of the node's x.
 */
const RESULT_LINES = [110.977, 162.305, 218.243, 275.962]

// Demo report `.home-figure-green`, verbatim. Figma's raster fill of the same
// tile is this gradient to within 0.3/255.
const GREEN = `url("data:image/svg+xml;utf8,<svg viewBox='0 0 247.33 194' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><rect x='0' y='0' height='100%' width='100%' fill='url(%23grad)' opacity='1'/><defs><radialGradient id='grad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' gradientTransform='matrix(-15.8 -23.5 30.139 -20.263 296.5 248)'><stop stop-color='rgba(182,209,152,1)' offset='0'/><stop stop-color='rgba(184,201,121,1)' offset='0.29291'/><stop stop-color='rgba(148,176,102,1)' offset='0.43046'/><stop stop-color='rgba(112,151,82,1)' offset='0.56801'/><stop stop-color='rgba(85,136,66,1)' offset='0.67601'/><stop stop-color='rgba(58,121,49,1)' offset='0.784'/><stop stop-color='rgba(32,107,33,1)' offset='0.892'/><stop stop-color='rgba(18,99,24,1)' offset='0.946'/><stop stop-color='rgba(5,92,16,1)' offset='1'/></radialGradient></defs></svg>")`

export function HomeV4ImmersiveTiles() {
  const locale = useLocale()
  const lang = locale.startsWith("de")
    ? "de"
    : locale.startsWith("fr")
      ? "fr"
      : "en"
  const s = labels[lang]
  // The report writes 4.8; French writes 4,8.
  const num = (n: number) =>
    lang === "fr" ? n.toFixed(1).replace(".", ",") : n.toFixed(1)

  return (
    <div className="imt">
      <div className="imt-grid">
        {/* 473:1751, the photo tile */}
        <div className="imt-tile imt-t1" style={order(0)}>
          <div className="imt-abs" style={{ inset: 0 }}>
            <Image
              src={img("tile-photo-phone-mail-20261009-v5.webp")}
              alt=""
              fill
              unoptimized
              className="object-cover"
            />
          </div>
        </div>

        {/* 473:1968, action plans */}
        <div className="imt-tile imt-t2" style={order(1)}>
          <TileBackground file="tile-plans-bg-v2.webp" />
          <p className="imt-heading imt-t2-heading">{s.plansHeading}</p>
          {/* 480:4040 */}
          <div className="imt-glass imt-plan">
            {s.plans.map(([title, text], i) => {
              const Icon = PLAN_ICONS[i]
              return (
                <div key={title} className="imt-plan-row">
                  <div className="imt-plan-icon">
                    <Icon />
                  </div>
                  <div className="imt-plan-info">
                    <p className="imt-plan-title">{title}</p>
                    <p className="imt-plan-text">{text}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* 473:1767, fasting glucose */}
        <div className="imt-tile imt-t3" style={order(2)}>
          <TileBackground file="tile-cholesterol-bg-v2.webp" />
          <div
            className="imt-chol-head"
            style={{ left: p(26.172), top: p(45) }}
          >
            <p className="imt-chol-title">{s.marker}</p>
            <span className="imt-chol-status">{s.normal}</span>
          </div>
          <p className="imt-chol-value" style={{ left: p(25), top: p(84.323) }}>
            {num(MARKER)}
          </p>
          <p
            className="imt-chol-unit"
            style={{ left: p(98.75), top: p(104.323) }}
          >
            MMOL/L
          </p>
          <MarkerChart />
          {HISTORY.map(({ v, label: [x, y] }, i) => (
            <p
              key={i}
              aria-hidden
              className="imt-chol-point"
              style={{ left: p(x), top: p(y) }}
            >
              {num(v)}
            </p>
          ))}
          {/* 484:10844, the reference range: high, normal, low */}
          <div
            aria-hidden
            className="imt-range"
            style={{ left: p(33), top: p(160), width: p(6), height: p(169) }}
          >
            <span className="imt-range-high" style={{ height: p(38.767) }} />
            <span className="imt-range-normal" />
            <span className="imt-range-high" style={{ height: p(49.753) }} />
          </div>
        </div>

        {/* 482:10478, your health report */}
        <div className="imt-tile imt-t4" style={order(3)}>
          <div className="imt-canvas">
            <Image
              src={img("tile-report-bg.webp")}
              alt=""
              fill
              unoptimized
              className="object-cover"
            />
            <p className="imt-heading imt-t4-heading">{s.reportHeading}</p>
            {/* 484:11404, the demo report's `home-report-block` */}
            <div className="imt-glass imt-report">
              <div className="imt-report-head">
                <span className="imt-report-icon">
                  <ReportIcon />
                </span>
                <span className="imt-label">{s.report}</span>
              </div>
              <div className="imt-stats">
                {/* Biological age, `.home-figure-green.home-bioage` */}
                <div className="imt-stat imt-bioage">
                  <div aria-hidden className="imt-bioage-overlay">
                    <Image
                      src={img("bioage-overlay.webp")}
                      alt=""
                      fill
                      unoptimized
                      className="object-cover"
                    />
                  </div>
                  <div className="imt-stat-body">
                    <p className="imt-stat-n">33</p>
                    <p className="imt-stat-l">{s.bioAge}</p>
                    <div className="imt-bar-wrap">
                      <div className="imt-bar">
                        <div className="imt-track" />
                        <div className="imt-delta" />
                        <div className="imt-pill">
                          <ArrowDownIcon />
                          <span>{s.years}</span>
                        </div>
                      </div>
                    </div>
                    <p className="imt-stat-foot">{s.bioCaption}</p>
                  </div>
                </div>
                {/* Findings */}
                <div className="imt-stat imt-stat-plain">
                  <div className="imt-stat-body">
                    <p className="imt-stat-n">14</p>
                    <p className="imt-stat-l">{s.findings}</p>
                    <div className="imt-bar-wrap">
                      <div className="imt-split">
                        <span
                          className="imt-deep"
                          style={{ width: "64.2857%" }}
                        />
                        <span
                          className="imt-light"
                          style={{ width: "35.7143%" }}
                        />
                      </div>
                    </div>
                    <div className="imt-stat-foot imt-legend">
                      <p>
                        <span className="imt-dot imt-light" />
                        <b>5</b>
                        <span>{s.minor}</span>
                      </p>
                      <p>
                        <span className="imt-dot imt-deep" />
                        <b>9</b>
                        <span>{s.observation}</span>
                      </p>
                    </div>
                  </div>
                </div>
                {/* Biomarkers */}
                <div className="imt-stat imt-stat-plain">
                  <div className="imt-stat-body">
                    <p className="imt-stat-n">100</p>
                    <p className="imt-stat-l">{s.biomarkers}</p>
                    <div className="imt-bar-wrap">
                      <div className="imt-split">
                        <span className="imt-deep" style={{ width: "13%" }} />
                        <span className="imt-light" style={{ width: "81%" }} />
                        <span className="imt-terra" style={{ width: "6%" }} />
                      </div>
                    </div>
                    <div className="imt-stat-foot imt-chips">
                      <p>
                        <b>13</b>
                        <span>{s.optimal}</span>
                      </p>
                      <p>
                        <b>81</b>
                        <span>{s.inRange}</span>
                      </p>
                      <p>
                        <b>6</b>
                        <span>{s.outOfRange}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* 484:11493, the doctor's note below, left empty in Figma; it
                only carries the panel past the tile's lower edge. */}
              <div className="imt-report-foot" />
            </div>
          </div>
        </div>

        {/* 482:10278, clinical follow-ups */}
        <div className="imt-tile imt-t5" style={order(4)}>
          <TileBackground file="tile-followups-bg-v2.webp" />
          {/* 484:11630 */}
          <div className="imt-doctor">
            <Image
              src={img("doctor-donati.webp")}
              alt=""
              width={86}
              height={84}
              unoptimized
              className="imt-doctor-photo"
            />
            <p>Prof. Dr. med. Olivio Donati</p>
          </div>
          {/* 484:11205, the action plan's clinical follow-ups */}
          <div className="imt-glass imt-followups">
            <div className="imt-followups-head">
              <span className="imt-followups-icon">
                <StethoscopeIcon />
              </span>
              <span className="imt-label imt-label-sm">{s.followUps}</span>
            </div>
            <div className="imt-followups-list">
              {FOLLOW_UP_IMAGES.map((files, i) => (
                <div key={files.join()} className="imt-followup">
                  <span className="imt-followup-img">
                    {files.map((file, j) => (
                      <Image
                        key={file}
                        src={img(file)}
                        alt=""
                        fill
                        unoptimized
                        className={j ? "object-contain" : "object-cover"}
                      />
                    ))}
                  </span>
                  <span className="imt-followup-text">
                    <span className="imt-followup-title">
                      {s.followUpItems[i][0]}
                    </span>
                    <span className="imt-followup-sub">
                      {s.followUpItems[i][1]}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{CSS}</style>
    </div>
  )
}

/** 484:11222 and 484:11246: the CT, then the CT under the brain scan. */
const FOLLOW_UP_IMAGES = [
  ["followup-ct.webp"],
  ["followup-ct.webp", "followup-brain.webp"],
]

/**
 * A tile's photo layer, as Figma renders it (2x, 832 × 722). Figma drew the
 * rounded tile on white, and that white showed in the corners through the
 * clip's anti-aliased rim; the v2 files carry the photo out into the corners
 * instead (Eugene, 29.09).
 */
function TileBackground({ file }: { file: string }) {
  return (
    <Image
      src={img(file)}
      alt=""
      fill
      unoptimized
      className="-z-10 object-cover"
    />
  )
}

/**
 * The chart in tile coordinates (416 × 361), drawn from Figma's own vectors:
 * the dashed result lines, today's line (484:10834), the glass band
 * (484:10804), the two connectors (484:10805, 484:10810) and the dots
 * (484:10895…99). The value labels sit over it as text.
 */
function MarkerChart() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 416 361"
      fill="none"
      preserveAspectRatio="none"
      className="imt-chart"
    >
      <defs>
        <linearGradient
          id="imt-chol-fade"
          x1="144.151"
          y1="6.19456"
          x2="0.00271182"
          y2="-41.3038"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#858585" stopOpacity="0.32" />
          <stop offset="1" stopColor="#858585" stopOpacity="0" />
        </linearGradient>
      </defs>
      {RESULT_LINES.map((x) => (
        <line
          key={x}
          x1={x}
          y1={336.527}
          x2={x}
          y2={191.875}
          stroke="white"
          strokeWidth={0.701281}
          strokeLinecap="round"
          strokeDasharray="4.21 4.21"
        />
      ))}
      <line
        x1={332.625}
        y1={336.378}
        x2={332.625}
        y2={192.024}
        opacity={0.6}
        stroke="white"
        strokeLinecap="round"
        strokeDasharray="2 2"
      />
      <g transform="translate(24.0703 201.7344)" opacity={0.77}>
        <path
          d="M0 5.6103C0 2.51184 2.51179 4.48744e-05 5.61025 4.44892e-05L363.476 0C366.574 -3.85195e-07 369.086 2.51179 369.086 5.61025V69.0499C369.086 72.1484 366.574 74.6602 363.476 74.6602H5.61027C2.51181 74.6602 0 72.1484 0 69.0499V5.6103Z"
          fill="white"
          fillOpacity={0.35}
        />
        <path
          d="M5.61035 0.499999H363.476C366.298 0.499999 368.586 2.78804 368.586 5.61035V69.0498C368.586 71.8721 366.298 74.1602 363.476 74.1602H5.61035C2.78804 74.1602 0.5 71.8721 0.5 69.0498V5.61035C0.5 2.78804 2.78804 0.5 5.61035 0.499999Z"
          stroke="white"
          strokeOpacity={0.8}
        />
      </g>
      <path
        transform="translate(219.3737 231.8286)"
        d="M0.158618 0.683108L56.9469 13.8694L112.664 2.88082"
        stroke="#A5A5A5"
        strokeOpacity={0.32}
        strokeWidth={1.40256}
      />
      <path
        transform="translate(74.3516 224.9437)"
        d="M144.151 7.56813L88.4343 9.76585L35.932 2.62328L0.0375165 0.700277"
        stroke="url(#imt-chol-fade)"
        strokeWidth={1.40256}
      />
      {HISTORY.map(({ dot: [cx, cy] }, i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={4.75928}
          fill="white"
          stroke="black"
          strokeWidth={3}
        />
      ))}
    </svg>
  )
}

/* Icons, Figma's own exports */

/** 480:4044, calcium. */
function CalciumIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      style={{ width: p(20), height: p(20) }}
      aria-hidden
    >
      <path
        d="M15.7891 2.45508C15.9985 2.42324 16.21 2.5307 16.3047 2.72754C16.3294 2.77899 16.8909 3.95581 17.2529 5.63574C17.4663 6.62624 17.5655 7.58835 17.5479 8.49512C17.5257 9.64134 17.3159 10.7028 16.9229 11.6494C16.5298 12.5962 15.9257 13.4951 15.1289 14.3213C14.4986 14.9747 13.7465 15.585 12.8936 16.1348C11.8085 16.8342 10.8448 17.2544 10.4082 17.4287L10.1621 17.5225C10.1096 17.5407 10.0548 17.5498 10 17.5498C9.94517 17.5498 9.89034 17.5408 9.83789 17.5225C9.78419 17.5037 8.55317 17.0673 7.10645 16.1348C6.25352 15.585 5.50126 14.9747 4.87109 14.3213C4.07437 13.4951 3.47026 12.5962 3.07715 11.6494C2.68422 10.7028 2.47429 9.64135 2.45215 8.49512C2.43463 7.58836 2.5337 6.62623 2.74707 5.63574C3.10903 3.95619 3.67032 2.77949 3.69531 2.72754L3.74316 2.64844C3.86882 2.47868 4.09375 2.40536 4.2998 2.47754C4.3535 2.49635 5.58451 2.93265 7.03125 3.86523C7.88429 4.41502 8.63632 5.02515 9.2666 5.67871C9.533 5.95502 9.77736 6.23973 10 6.53125C11.0435 5.16794 12.4055 4.18959 13.4209 3.58496L13.875 3.3252C14.8999 2.76093 15.6622 2.49078 15.7002 2.47754L15.7891 2.45508ZM4.39941 3.57227C4.21172 4.03964 3.91625 4.86103 3.70215 5.8623C3.39669 7.2909 3.19908 9.38633 3.9834 11.2754C4.7676 13.1641 6.39171 14.5061 7.62012 15.3008C8.15373 15.6458 8.66132 15.9191 9.08008 16.125L5.2168 6.81738C5.11309 6.56763 5.23163 6.28137 5.48145 6.17773C5.73158 6.07429 6.01942 6.19264 6.12305 6.44238L9.9873 15.751C10.5137 14.2029 11.2142 11.2778 10.1543 8.72461C9.99804 8.34836 9.80848 7.99355 9.59473 7.65918L9.5791 7.63477V7.63379C8.08997 5.3241 5.43355 4.01486 4.39941 3.57227ZM15.5986 3.57129C15.2117 3.7364 14.5977 4.0235 13.9062 4.43652C12.9042 5.03505 11.5419 6.0217 10.5811 7.39453C10.7647 7.70701 10.9257 8.02589 11.0605 8.35059C11.4816 9.3649 11.6876 10.4936 11.6855 11.7217L14.3262 5.36133C14.4298 5.11162 14.7166 4.99342 14.9668 5.09668C15.217 5.20002 15.3362 5.48653 15.2324 5.73633L10.9189 16.125C11.338 15.9191 11.8458 15.6462 12.3799 15.3008C13.6083 14.5061 15.2324 13.1641 16.0166 11.2754C16.8009 9.38633 16.6033 7.2909 16.2979 5.8623C16.0835 4.85995 15.7863 4.03836 15.5986 3.57129Z"
        fill="#2D2D2D"
        stroke="#2D2D2D"
        strokeWidth={0.1}
      />
    </svg>
  )
}

/** 480:4053, exercise. */
function ExerciseIcon() {
  return (
    <svg
      viewBox="0 0 20.8515 20.8515"
      fill="none"
      style={{ width: p(20.851), height: p(20.851) }}
      aria-hidden
    >
      <path
        d="M5.24076 3.41848C5.93792 3.41868 6.4954 4.03819 6.49564 4.78665V7.76321H13.5044V4.78665C13.5047 4.03817 14.0621 3.41864 14.7593 3.41848H15.5533C16.2504 3.41873 16.8079 4.03822 16.8081 4.78665V6.07766C16.9127 6.04489 17.0233 6.02495 17.1392 6.02493H17.9322C18.6295 6.02493 19.1878 6.64451 19.188 7.39309V12.606C19.188 13.3548 18.6296 13.9751 17.9322 13.9751H17.1392C17.0232 13.9751 16.9127 13.9542 16.8081 13.9214V15.2124C16.8081 15.9611 16.2505 16.5813 15.5533 16.5816H14.7593C14.062 16.5814 13.5044 15.9611 13.5044 15.2124V12.2368H6.49564V15.2124C6.49564 15.9611 5.93807 16.5814 5.24076 16.5816H4.44681C3.74951 16.5814 3.19193 15.9611 3.19193 15.2124V13.9214C3.08733 13.9542 2.97684 13.9751 2.86087 13.9751H2.06693C1.36967 13.9749 0.812046 13.3546 0.812046 12.606V7.39309C0.812281 6.64467 1.36981 6.02519 2.06693 6.02493H2.86087C2.97674 6.02495 3.08741 6.04488 3.19193 6.07766V4.78665C3.19217 4.0382 3.74966 3.41869 4.44681 3.41848H5.24076ZM4.44681 4.4175C4.26999 4.41771 4.11599 4.57794 4.11576 4.78665V15.2124C4.11576 15.4214 4.26985 15.5823 4.44681 15.5825H5.24076C5.41772 15.5823 5.57181 15.4214 5.57181 15.2124V4.78665C5.57157 4.57794 5.41758 4.41771 5.24076 4.4175H4.44681ZM14.7593 4.4175C14.5825 4.41766 14.4285 4.57791 14.4283 4.78665V15.2124C14.4283 15.4214 14.5823 15.5824 14.7593 15.5825H15.5533C15.7302 15.5823 15.8843 15.4213 15.8843 15.2124V4.78665C15.8841 4.57797 15.7301 4.41776 15.5533 4.4175H14.7593ZM2.06693 7.02395C1.89014 7.02422 1.73611 7.18442 1.73587 7.39309V12.606C1.73587 12.8149 1.89001 12.9758 2.06693 12.9761H2.86087C3.03788 12.976 3.19193 12.815 3.19193 12.606V7.39309C3.1917 7.18434 3.03774 7.0241 2.86087 7.02395H2.06693ZM17.1392 7.02395C16.9623 7.02405 16.8084 7.18431 16.8081 7.39309V12.606C16.8081 12.815 16.9622 12.976 17.1392 12.9761H17.9322C18.1093 12.9761 18.2642 12.8151 18.2642 12.606V7.39309C18.264 7.18425 18.1091 7.02395 17.9322 7.02395H17.1392ZM6.49564 11.2378H13.5044V8.76223H6.49564V11.2378Z"
        fill="#2D2D2D"
        stroke="#2D2D2D"
        strokeWidth={0.130322}
      />
    </svg>
  )
}

/** 480:4062, re-test. */
function RetestIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      style={{ width: p(24), height: p(24) }}
      aria-hidden
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.0226 4.02429H14.893V19.5174C14.8932 21.162 13.5952 22.5 11.9999 22.5C10.4045 22.5 9.10658 21.162 9.10658 19.5175L9.10667 4.0243H8.97747C8.7137 4.0243 8.5 3.80405 8.5 3.53213V1.99217C8.5 1.72025 8.7137 1.5 8.97747 1.5L15.0226 1.5C15.2863 1.5 15.5 1.72025 15.5 1.99217V3.53218C15.5 3.80405 15.2862 4.02429 15.0226 4.02429ZM13.9381 4.02429H10.0615V11.3809C11.617 11.1039 12.2926 11.6522 12.5857 12.1983C12.8027 12.602 13.2571 12.7616 13.9382 12.6748L13.9381 4.02429ZM10.0615 12.3827L10.0615 19.5174C10.0615 20.6192 10.931 21.5155 11.9998 21.5155C13.0687 21.5155 13.9382 20.6192 13.9382 19.5174L13.9382 13.6651C12.8145 13.7753 12.1156 13.3553 11.7504 12.6753C11.4908 12.1918 10.6868 12.25 10.0615 12.3827ZM9.45474 3.03987L14.5451 3.03987V2.48434L9.45474 2.48434V3.03987Z"
        fill="#2D2D2D"
      />
    </svg>
  )
}

const PLAN_ICONS = [CalciumIcon, ExerciseIcon, RetestIcon]

/** 484:11408, the report block's clipboard (tabler), at Figma's size. */
function ReportIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-aubergine-800)"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: p(15.738), height: p(15.738) }}
      aria-hidden
    >
      <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
      <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
      <path d="M9 17v-5" />
      <path d="M12 17v-1" />
      <path d="M15 17v-3" />
    </svg>
  )
}

/** 484:11436, lucide arrow-down at stroke 3, as `.home-bioage` sets it. */
function ArrowDownIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: p(9.94), height: p(13.253) }}
      aria-hidden
    >
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  )
}

/** 484:11209, lucide stethoscope, white. */
function StethoscopeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: p(12.349), height: p(12.349) }}
      aria-hidden
    >
      <path d="M11 2v2" />
      <path d="M5 2v2" />
      <path d="M5 3H4a2 2 0 0 0-2 2v4a6 6 0 0 0 12 0V5a2 2 0 0 0-2-2h-1" />
      <path d="M8 15a6 6 0 0 0 12 0v-3" />
      <circle cx="20" cy="10" r="2" />
    </svg>
  )
}

const CSS = `
  /* The grid spans the text column (Eugene, 29.09) and sits 49 above the
     trust row that follows it, as in the frame; a percentage margin is a
     share of the column's width, so the gap scales with the tiles. */
  .imt{position:relative;width:100%;margin-bottom:calc(100% * 49 / 1280);container-type:inline-size;color:var(--color-neutral-900)}
  .imt-grid{--px:calc(100cqw / 1280);display:grid;grid-template-columns:repeat(3,${p(416)});grid-template-rows:repeat(2,${p(361)});column-gap:${p(16)};row-gap:${p(21)}}
  /* No fill under the photos: a colour under an image that covers the tile
     shows through the anti-aliased rim of its rounded clip, which drew a
     light hairline round the corners (Eugene, 29.09).
     Each tile takes its share of the section's --reveal with 05's stagger
     (0.07) and window (0.45); unitless throughout, since a clamp() that mixes
     a number with a length is dropped whole. */
  .imt-tile{--t:clamp(0, calc((var(--reveal, 1) - var(--i, 0) * .07) / .45), 1);position:relative;isolation:isolate;overflow:hidden;border-radius:${p(18)};opacity:var(--t);transform:translateY(calc((1 - var(--t)) * 48px))}
  .imt-t4{grid-column:span 2}
  .imt-canvas{position:absolute;inset:0}
  .imt-abs,.imt-heading,.imt-glass,.imt-chol-head,.imt-chol-value,.imt-chol-unit,.imt-chol-point,.imt-range,.imt-doctor{position:absolute}
  .imt-abs{overflow:hidden;border-radius:${p(24)}}
  /* Captions centre on the panel under them. */
  .imt-heading{translate:-50% 0;font-size:${p(16)};line-height:${p(19.2)};font-weight:500;text-align:center;white-space:nowrap}
  .imt-t2-heading{left:${p(208)};top:${p(45.75)}}
  .imt-t4-heading{left:${p(427.196)};top:${p(45.27)};width:${p(520)};white-space:normal;text-wrap:balance}

  /* Figma's glass: white 47 %, a hairline inside, 11.48 background blur
     (4.81 in CSS, Figma's radius over 2.385 as fitted on the section photo).
     Hairlines are inset shadows, as a border under 1 device pixel gets
     snapped and moves the content; the report and follow-up panels count the
     line in their padding, the plans panel does not. */
  .imt-glass{display:flex;flex-direction:column;padding:${p(13.302)};gap:${p(9.452)};background:rgba(255,255,255,.47);box-shadow:inset 0 0 0 ${p(0.394)} rgba(255,255,255,.84);border-radius:${p(24)};-webkit-backdrop-filter:blur(${p(4.814)});backdrop-filter:blur(${p(4.814)})}
  .imt-report{left:${p(89.25)};top:${p(126)};width:${p(675.891)};padding:${p(13.696)}}
  /* 484:11493, the doctor's note below, left empty in Figma; it only carries
     the panel past the tile's lower edge. */
  .imt-report-foot{height:${p(67)}}

  /* Figma's imported text boxes are whole pixels tall (37 for a 36.4 line,
     27 for two lines of 13.25); the heights below keep its layout. Its
     renderer also sets text under 12.5 px about 0.9 lower in the line than
     Chrome does (measured against its 2x render), so that text moves down
     by as much, with translate: Chrome rounds a laid-out baseline to the
     whole pixel and would swallow the shift. */
  .imt-plan-text,.imt-stat-l,p.imt-stat-foot,.imt-legend b,.imt-legend b+span,.imt-chips b,.imt-chips b+span,.imt-pill span,.imt-label-sm,.imt-followup-title,.imt-followup-sub{translate:0 ${p(0.9)}}

  /* 480:4040 */
  .imt-plan{left:${p(49.5)};top:${p(101)};width:${p(317)};gap:${p(6)}}
  .imt-plan-row{display:flex;align-items:flex-start;gap:${p(10.043)};padding:${p(12)} ${p(10.043)};border-radius:${p(10.043)};background:white}
  .imt-plan-icon{display:flex;align-self:stretch;flex:none;align-items:center;justify-content:center;width:${p(32)}}
  .imt-plan-icon svg{display:block}
  .imt-plan-info{display:flex;flex:1;min-width:0;flex-direction:column;gap:${p(2.511)}}
  .imt-plan-title{font-size:${p(15)};line-height:${p(18)};letter-spacing:${p(-0.23)};font-weight:500}
  .imt-plan-text{min-height:${p(13)};padding-right:${p(15.065)};font-size:${p(12)};line-height:${p(12.554)};color:var(--color-neutral-600)}

  /* 473:1767 */
  .imt-chol-head{display:flex;align-items:flex-start;gap:${p(9.328)};color:#101828}
  .imt-chol-title{margin-top:${p(3)};font-size:${p(20)};line-height:${p(15.4)};font-weight:500;white-space:nowrap}
  .imt-chol-status{display:flex;align-items:center;height:${p(21)};padding:${p(2)} ${p(8)};border-radius:${p(40)};background:var(--color-light-green-500);color:var(--color-light-green-50);font-size:${p(11)};line-height:${p(13)};font-weight:500}
  .imt-chol-value{font-size:${p(48)};line-height:${p(32)};font-weight:500;color:#101828;white-space:nowrap}
  .imt-chol-unit{font-size:${p(16)};line-height:${p(12)};letter-spacing:${p(0.45)};color:#101828;white-space:nowrap}
  .imt-chart{position:absolute;inset:0;width:100%;height:100%;overflow:visible}
  .imt-chol-point{font-size:${p(12)};line-height:1;font-weight:500;white-space:nowrap}
  .imt-range{display:flex;flex-direction:column;gap:${p(5.61)}}
  .imt-range span{display:block;flex:none;border-radius:${p(28.051)};opacity:.8}
  .imt-range-high{background:#e96f3f}
  .imt-range-normal{flex:1 1 0!important;background:var(--color-light-green-500)}

  /* 484:11404, the demo report's health report block */
  .imt-report-head{display:flex;align-items:center;gap:${p(8.283)}}
  .imt-report-icon{display:flex;flex:none;align-items:center;justify-content:center;width:${p(26.506)};height:${p(26.506)};border-radius:${p(6.626)};background:white}
  .imt-label{min-height:${p(15)};font-size:${p(11.596)};line-height:${p(14.495)};letter-spacing:${p(0.2899)};font-weight:500;text-transform:uppercase;white-space:nowrap}
  .imt-stats{display:flex;gap:${p(9.94)}}
  .imt-stat{position:relative;display:flex;flex:1 1 0;min-width:0;height:${p(160.69)};flex-direction:column;overflow:hidden;padding:${p(13.253)};border-radius:${p(13.253)}}
  .imt-stat-plain{background:var(--color-neutral-50)}
  .imt-stat-body{position:relative;display:flex;width:${p(178.912)};min-height:0;flex-grow:1;flex-direction:column}
  .imt-stat-body>*{flex-shrink:0}
  .imt-stat-n{height:${p(37)};font-size:${p(36.445)};line-height:${p(36.445)};font-weight:500;white-space:nowrap}
  .imt-stat-l{height:${p(19.879)};font-size:${p(11.596)};line-height:${p(14.909)};white-space:nowrap}
  .imt-bar-wrap{position:relative;flex:none;padding-top:${p(16.566)}}
  .imt-stat-foot{margin-top:auto;padding-top:${p(13.253)};font-size:${p(10.768)};line-height:${p(13.253)}}
  p.imt-stat-foot{min-height:${p(40.253)}}
  .imt-split{display:flex;height:${p(6.626)};gap:${p(1.657)}}
  .imt-split span{display:block;height:100%;min-width:${p(8.283)};border-radius:9999px}
  .imt-deep{background:var(--color-deep-green-500)}.imt-light{background:var(--color-light-green-400)}.imt-terra{background:var(--color-terracotta-300)}
  .imt-legend{display:flex;flex-direction:column;gap:${p(4.97)}}
  .imt-legend p{position:relative;display:flex;height:${p(13.253)};gap:${p(4.97)};padding-left:${p(9.94)};white-space:nowrap}
  .imt-legend .imt-dot{position:absolute;left:0;top:${p(3.73)};width:${p(4.97)};height:${p(4.97)};border-radius:9999px}
  .imt-legend b,.imt-chips b{font-weight:500}
  /* Figma sizes the chips around text boxes rounded up to whole pixels;
     half a pixel after each word keeps their widths on average. */
  .imt-chips b,.imt-chips b+span{padding-right:${p(0.45)}}
  .imt-chips{display:flex;height:${p(54.668)};flex-wrap:wrap;align-content:flex-start;gap:${p(5.879)} ${p(6.027)}}
  .imt-chips p{display:flex;align-items:flex-start;gap:${p(4.97)};height:${p(18.141)};padding:${p(2.485)} ${p(7.454)} ${p(1.656)};box-shadow:inset 0 0 0 ${p(0.828)} #eaeaea;border-radius:${p(33.132)};white-space:nowrap}

  /* Biological age: \`.home-figure-green\` and \`.home-bioage\` */
  .imt-bioage{flex:none;width:${p(209.928)};color:white;background-image:${GREEN};background-repeat:no-repeat;background-size:100% 100%}
  /* Figma's -49.42, -15.73, 303.15 × 226.953 on the 209.928 × 160.69 card,
     as shares of the card, so a wider card on a phone stays covered. */
  .imt-bioage-overlay{position:absolute;left:-23.541%;top:-9.789%;width:144.407%;height:141.237%;opacity:.41;pointer-events:none}
  .imt-bar{position:relative;height:${p(6.626)}}
  .imt-track{position:absolute;inset:0;border-radius:${p(33.132)};background:rgba(255,255,255,.3)}
  /* The marker's 58.81, 34.788 and 54.642 on the 178.912 bar, as shares of
     the bar for the same reason. */
  .imt-delta{position:absolute;top:0;left:32.871%;width:19.444%;height:100%;border-radius:${p(33.132)};background:white}
  .imt-pill{position:absolute;top:50%;left:30.541%;display:flex;align-items:center;height:${p(16.566)};padding:0 ${p(4.141)} 0 ${p(3.313)};translate:-50% -50%;border-radius:${p(33.132)};background:white;color:var(--color-neutral-900);white-space:nowrap}
  .imt-pill svg{flex:none}
  .imt-pill span{min-height:${p(12)};font-size:${p(9.111)};line-height:${p(11.596)};font-weight:700}

  /* 482:10278, the action plan's clinical follow-ups */
  .imt-doctor{left:${p(50.203)};top:${p(42.55)};display:flex;align-items:center;gap:${p(10.909)}}
  .imt-doctor-photo{width:${p(43)};height:${p(42)};border-radius:${p(90.909)};outline:${p(1)} solid rgba(255,255,255,.48);outline-offset:${p(-0.5)};object-fit:cover}
  .imt-doctor p{min-height:${p(19)};font-size:${p(14)};line-height:${p(18.182)};letter-spacing:${p(0.0909)};font-weight:500;white-space:nowrap}
  .imt-followups{left:${p(49.5)};top:${p(115.281)};width:${p(317)};padding:${p(14.302)};box-shadow:inset 0 0 0 ${p(1)} rgba(255,255,255,.66);border-radius:${p(18)}}
  .imt-followups-head{display:flex;align-items:center;gap:${p(7.718)}}
  .imt-followups-icon{display:flex;flex:none;align-items:center;justify-content:center;width:${p(21.611)};height:${p(21.611)};border-radius:${p(6.174)};background:var(--color-aubergine-700)}
  .imt-label-sm{min-height:${p(14)};font-size:${p(10.805)};line-height:${p(13.506)};letter-spacing:${p(0.2701)}}
  .imt-followups-list{display:flex;height:${p(144)};flex-direction:column}
  .imt-followup{display:flex;flex:none;align-items:center;gap:${p(9.262)};width:${p(330.33)};padding:${p(9.262)} 0}
  .imt-followup-img{position:relative;flex:none;width:${p(56)};height:${p(56)};overflow:hidden;border-radius:${p(9.262)}}
  .imt-followup-text{display:flex;min-width:0;flex:1;flex-direction:column;gap:${p(3.087)}}
  .imt-followup-title{min-height:${p(17)};font-size:${p(12.349)};line-height:${p(16.98)};font-weight:500;white-space:nowrap}
  .imt-followup-sub{width:${p(219.191)};min-height:${p(28)};font-size:${p(10.805)};line-height:${p(13.506)};color:#71717b}

  @media (min-width:1024px){.imt-tile{will-change:opacity,transform}}

  /* Two tiles a row, the report tile across both; same design, one scale.
     Nothing is pinned here, so a tile below the fold rises in once as it
     comes into view (\`data-wait\`, set and cleared by the section). */
  @media (max-width:1023px){
    .imt{margin-bottom:0}
    .imt-grid{--px:calc(100cqw / 848);grid-template-columns:repeat(2,${p(416)});grid-template-rows:none;grid-auto-rows:${p(361)};grid-auto-flow:dense}
    .imt-tile{--t:1;transition:opacity .7s cubic-bezier(.2,.7,.3,1),transform .7s cubic-bezier(.2,.7,.3,1)}
    .imt-tile[data-wait]{opacity:0;transform:translateY(32px)}
  }
  /* One tile a row. The report tile would not fit Figma's layout at the
     others' scale, so it stacks: caption, then the panel with its three
     figures one under another, all of it in view (Eugene, 29.09). */
  @media (max-width:600px){
    .imt-grid{--px:calc(100cqw / 416);grid-template-columns:${p(416)};grid-auto-rows:auto;row-gap:${p(16)}}
    .imt-tile{height:${p(361)}}
    .imt-t4{grid-column:auto;height:auto}
    .imt-canvas{position:relative;inset:auto;padding:${p(45.27)} ${p(20)} ${p(20)}}
    .imt-t4-heading{position:relative;left:auto;top:auto;width:auto;translate:none}
    .imt-report{position:relative;left:auto;top:auto;width:auto;margin-top:${p(36)}}
    .imt-stats{flex-direction:column}
    .imt-stat{flex:none}
    .imt-bioage,.imt-stat-body{width:auto}
    /* The chips fit one row on the wider card; at the foot, like the legend. */
    .imt-chips{height:auto}
    .imt-report-foot{display:none}
    /* Read on a phone, the plan, the report and the follow-ups were 9 to 12px
       type at the shared scale. These three draw at 1.3 times it and stack
       in flow, heading then panel the tile's width, so the bigger panel
       still fits (Eugene, 15.10). */
    .imt-t2,.imt-t4,.imt-t5{--px:calc(100cqw / 416 * 1.3)}
    .imt-t2,.imt-t5{height:auto;padding:${p(34)} ${p(14)} ${p(14)}}
    .imt-canvas{padding:${p(34)} ${p(14)} ${p(14)}}
    .imt-t2-heading,.imt-plan,.imt-doctor,.imt-followups{position:relative;left:auto;top:auto;translate:none}
    .imt-plan,.imt-followups{width:auto}
    .imt-plan,.imt-followups,.imt-report{margin-top:${p(22)}}
    .imt-followups-list{height:auto}
    .imt-followup,.imt-followup-sub{width:auto}
    .imt-followup-title{white-space:normal}
    /* The photo opens nothing on a phone: the tiles that say something come
       first and it closes the run (Eugene, 15.10). */
    .imt-t1{order:1}
  }
  @media (prefers-reduced-motion:reduce){
    .imt-tile{--t:1;transition:none;will-change:auto}
    .imt-tile[data-wait]{opacity:1;transform:none}
  }
`
